import {
  cacheKeys,
  isAssetReadOnly,
  MediaPickerComponent,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useProduct,
  useUpdateAsset,
  useWorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from 'react-native-paper';
import { array, boolean, string, object as yupObject } from 'yup';
import { basePadding } from '../../styles/spacing';
import { Toggle } from '../crop-circle/Components/Toggle';
import { tokens } from '../crop-circle/tokens';
import {
  ControlledCheckboxItems,
  ControlledTextInput,
  SaveFormBottom,
  SaveFormComponent,
} from '../forms';
import { GroupSelector } from '../group';
import { KeyboardAwareScrollView } from '../keyboard/keyboardAwareScrollView';
import { BottomComponent } from '../navigation';
import { AssetTagGallery } from './assetTagGallery';
import { DeleteAsset } from './deleteAsset';
import { PinnedActivities } from './pinnedActivities';
import { TagPickerComponent } from './types';
import { useAssetTags } from './useAssetTags';
import { usePinnedActivities } from './usePinnedActivities';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type EditAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type EditAssetProps = {
  assetId: string;
  mediaPicker: MediaPickerComponent;
  tagPicker?: TagPickerComponent;
  saveForm: SaveFormComponent;
  formHasBorder?: boolean;
  styles: StyleSheet.NamedStyles<EditAssetStyles>;
  allowsEditingCover?: boolean;
  bottomBar?: BottomComponent;
  onDeleted: () => Promise<void>;
  onActivityPress?: (activityId: string) => void;
  onCancel?: () => void;
  /** Opens with the cursor in Description, for the asset page's "+ Add description". */
  autoFocusDescription?: boolean;
};

type AssetTagItem = {
  id: string;
};

type AddonItem = {
  id: string;
  name: string;
};

export const EditAsset = (props: EditAssetProps) => {
  return <EditAssetBase {...props} />;
};

export const EditAssetBase = (props: EditAssetProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace(true);
  const isOEM = workspace?.type === WorkspaceType.Oem;
  const { account } = useCurrentAccount();
  const { updateAsset } = useUpdateAsset({
    assetId: props.assetId,
  });
  const { workspaceAsset } = useWorkspaceAsset(props.assetId);
  const lastAppliedFormValuesKey = useRef<string | undefined>(undefined);
  const tagIdsRef = useRef<string[] | undefined>(undefined);

  const isReadOnly = isAssetReadOnly(account, workspaceAsset);

  // Get workspace from workspaceAsset (more reliable)
  const workspaceId = workspaceAsset?.workspace?.id;
  const { groups: availableGroups } = useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();
  const asset = workspaceAsset?.asset;
  const { product } = useProduct(asset?.product?.id || '');
  const { assetTags, isLoaded: tagsLoaded } = useAssetTags(props.assetId);
  const { pinnedActivities } = usePinnedActivities(props.assetId);
  const [showAllPinnedActivities, setShowAllPinnedActivities] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  // Initialize selectedGroups when workspaceAsset changes
  useEffect(() => {
    if (workspaceAsset?.groups) {
      setSelectedGroups(
        workspaceAsset.groups.map((group) => ({
          id: group.id,
          name: group.name,
        }))
      );
    }
  }, [workspaceAsset?.groups]);

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    coverImageId: string().optional().nullable(),
    mediaIds: array().of(string()).optional().nullable(),
    tagIds: array().of(string()).optional().nullable(),
    groupIds: array().of(string()).optional().nullable(),
    addons: array().of(string()).optional().nullable(),
    product: string().optional().nullable(),
    serialNumber: string().optional().nullable(),
    hideReportAndNoteOptionsAnonymous: boolean().optional(),
  });

  const formValues = useMemo(
    () => ({
      name: asset?.name ?? '',
      coverImageId: asset?.coverImage ? asset?.coverImage.id : undefined,
      mediaIds: asset?.media?.map((media) => media.id),
      description: asset?.description ?? '',
      details: asset?.details ?? '',
      tagIds: tagsLoaded ? assetTags.map((tag) => tag.id) : undefined,
      groupIds: workspaceAsset?.groups?.map((group) => group.id),
      addons: asset?.addons?.map((addon) => addon.id),
      product: asset?.product?.name ?? '',
      serialNumber: asset?.serialNumber ?? '',
      hideReportAndNoteOptionsAnonymous:
        workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
    }),
    [
      asset?.addons,
      asset?.coverImage,
      asset?.description,
      asset?.details,
      asset?.media,
      asset?.name,
      asset?.product?.name,
      asset?.serialNumber,
      assetTags,
      tagsLoaded,
      workspaceAsset?.anonymousOptions?.hideReportAndNote,
      workspaceAsset?.groups,
    ]
  );
  const formValuesKey = useMemo(() => JSON.stringify(formValues), [formValues]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: formValues,
  });

  useEffect(() => {
    if (
      !asset ||
      isDirty ||
      lastAppliedFormValuesKey.current === formValuesKey
    ) {
      return;
    }

    reset(formValues);
    tagIdsRef.current = formValues.tagIds;
    lastAppliedFormValuesKey.current = formValuesKey;
  }, [asset, formValues, formValuesKey, isDirty, reset]);

  const addAssetTag = (tag: AssetTagItem) => {
    const tagIds = tagIdsRef.current ?? getValues('tagIds') ?? [];
    const nextTagIds = tagIds.includes(tag.id) ? tagIds : [...tagIds, tag.id];

    tagIdsRef.current = nextTagIds;
    setValue('tagIds', nextTagIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const deleteAssetTag = (tagId: string) => {
    const tagIds = tagIdsRef.current ?? getValues('tagIds') ?? [];
    const nextTagIds = tagIds.filter((id) => id !== tagId);

    tagIdsRef.current = nextTagIds;
    setValue('tagIds', nextTagIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (data: Record<string, unknown>) => {
    const { product, ...submissionData } = data;

    const input = {
      ...submissionData,
      tagIds: tagIdsRef.current ?? submissionData.tagIds,
      groupIds: selectedGroups.map((group) => group.id),
      id: props.assetId,
    };
    const savedFormValues = {
      ...data,
      tagIds: input.tagIds,
      groupIds: input.groupIds,
    };

    try {
      setIsSubmitting(true);

      const invokeUpdateAsset = updateAsset as unknown as (
        variables: { input: typeof input },
        options?: { onSuccess?: () => Promise<void> }
      ) => Promise<unknown>;

      await invokeUpdateAsset(
        { input: input },
        {
          onSuccess: async () => {
            reset(savedFormValues);
            lastAppliedFormValuesKey.current = formValuesKey;
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: cacheKeys('workspaceAsset').all,
              }),
              queryClient.invalidateQueries({
                queryKey: cacheKeys('tag').all,
              }),
            ]);
          },
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const issueTypeTitle = t('asset.editTitle');

  if (!props.bottomBar) {
    return null;
  }

  return (
    <SaveFormBottom
      isDirty={isDirty}
      isValid={isValid && !isSubmitting}
      onSubmit={async () => {
        await handleSubmit(onSubmit)();
      }}
      title={issueTypeTitle}
      saveLabel={t('asset.save')}
      bottomBar={props.bottomBar}
      onCancel={props.onCancel}
    >
      <KeyboardAwareScrollView
        containerStyle={props.styles.container}
        scrollEnabled
        showsVerticalScrollIndicator
        style={props.styles.contentContainer}
      >
        {/* Bottom clearance so the last item ("Delete Asset") clears the
            floating Save bar (Appbar at bottom: 22, ~64pt tall). 90 left it
            flush against the bar and clipped — GRIPP #2842. */}
        <View style={{ paddingBottom: 140, gap: 14 }}>
          {isOEM && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.productType')}
                </Text>
              </View>
              <ControlledTextInput
                control={control}
                name="product"
                editable={false}
                hideBorder={true}
              />
            </View>
          )}
          <View style={styles.formItem}>
            <View>
              <Text
                variant={'titleMedium'}
                style={[
                  tokens.typography.label.largeEmphasised,
                  styles.formItemTitle,
                ]}
              >
                {t('asset.name')}
              </Text>
            </View>
            <ControlledTextInput
              control={control}
              placeholder={t('asset.namePlaceholder')}
              name="name"
              editable={!isReadOnly}
              hideBorder={true}
            />
          </View>
          {!isOEM && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.description')}
                </Text>
              </View>
              <ControlledTextInput
                control={control}
                placeholder={t('asset.descriptionPlaceholder')}
                textArea={false}
                name="description"
                editable={!isReadOnly}
                autoFocus={props.autoFocusDescription}
                hideBorder={true}
              />
            </View>
          )}
          {!isOEM && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.details')}
                </Text>
              </View>
              <ControlledTextInput
                control={control}
                placeholder={t('asset.detailsPlaceHolder')}
                textArea={true}
                name="details"
                editable={!isReadOnly}
                autoExpand
                hideBorder={true}
              />
            </View>
          )}

          {/* Pinned Activities Section */}
          <PinnedActivities
            assetId={props.assetId}
            pinnedActivities={pinnedActivities}
            showAll={showAllPinnedActivities}
            onViewAllPress={() => setShowAllPinnedActivities(true)}
            onActivityPress={props.onActivityPress}
          />

          {isOEM && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.serialNumber')}
                </Text>
              </View>
              <ControlledTextInput
                control={control}
                name="serialNumber"
                editable={!isReadOnly}
                hideBorder={true}
              />
            </View>
          )}
          {isOEM && product && product.addons && product.addons.length > 0 && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.addons', { count: asset?.addons?.length || 0 })}
                </Text>
              </View>
              <View style={styles.addonsContainer}>
                <ControlledCheckboxItems
                  control={control as unknown as Control<FieldValues>}
                  name="addons"
                  options={(product.addons as AddonItem[])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((addon) => ({
                      label: addon.name,
                      value: addon.id,
                    }))}
                  position="leading"
                  style={styles.checkboxItems}
                  labelStyle={styles.checkboxItemsLabel}
                />
              </View>
            </View>
          )}
          {!isReadOnly && (
            <View style={styles.formItem}>
              <View>
                <Text
                  variant={'titleMedium'}
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.groups')}
                </Text>
              </View>
              <GroupSelector
                selectedGroups={selectedGroups}
                availableGroups={availableGroups}
                onGroupsChange={(groups) => {
                  setSelectedGroups(groups);
                  setValue(
                    'groupIds',
                    groups.map((g) => g.id),
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
                onCreateGroup={createGroup}
                disabled={false}
                onSelectorEdit={() =>
                  router.push(
                    `(modals)/assets/${props.assetId}/assetGroupReassign`
                  )
                }
              />
            </View>
          )}
          <View style={styles.formItem}>
            <View>
              <Text
                variant={'titleMedium'}
                style={[
                  tokens.typography.label.largeEmphasised,
                  styles.formItemTitle,
                ]}
              >
                {t('asset.tagCode')}
              </Text>
            </View>
            <AssetTagGallery
              existingAssetTags={assetTags}
              onAssetTagAdded={addAssetTag}
              onAssetTagDeleted={deleteAssetTag}
              tagPicker={props.tagPicker}
              editable={!isReadOnly && tagsLoaded}
            />
          </View>
          {!isReadOnly && (
            <View style={styles.formItem}>
              <View>
                <Text
                  style={[
                    tokens.typography.label.largeEmphasised,
                    styles.formItemTitle,
                  ]}
                >
                  {t('asset.hideReportAndNote')}
                </Text>
              </View>
              <Controller
                control={control}
                name="hideReportAndNoteOptionsAnonymous"
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value}
                    onValueChange={onChange}
                    label={t('asset.hideForAnonymousVisitors')}
                    labelStyle={tokens.typography.body.small}
                    size="small"
                  />
                )}
              />
            </View>
          )}
          {!isReadOnly && workspaceAsset && (
            <View style={[styles.deleteAssetContainer]}>
              <DeleteAsset
                workspaceAsset={workspaceAsset}
                onDeleted={props.onDeleted}
              />
            </View>
          )}
        </View>
      </KeyboardAwareScrollView>
    </SaveFormBottom>
  );
};

const styles = themedStyleSheet((role) => ({
  addonsContainer: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    gap: 0,
    margin: 0,
    padding: 0,
  },
  formItem: {
    width: '100%',
    paddingHorizontal: tokens.spacing.lg,
    marginBottom: tokens.spacing.sm,
  },
  formItemTitle: {
    fontWeight: tokens.typography.body.mediumEmphasised.fontWeight,
    marginBottom: tokens.spacing.xs,
    color: role.textStrong,
    flex: 1, // Allow title to take up available space
  },
  selectionsDropdown: {
    paddingHorizontal: 10,
    paddingVertical: tokens.spacing.sm,
    borderWidth: 1,
    borderColor: role.borderEmphasis,
    borderRadius: tokens.borderRadius.lg,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },

  checkboxItems: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  hideReportLabel: {
    paddingLeft: 0,
    textAlign: 'left',
    ...tokens.typography.label.large,
    color: role.textStrong,
  },
  hideReport: {
    paddingLeft: 0,
  },
  checkboxItemsLabel: {
    ...tokens.typography.body.medium,
    color: role.textStrong,
    textAlign: 'left',
  },
  deleteAssetContainer: {
    paddingHorizontal: basePadding.paddingHorizontal,
    paddingTop: 10,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.sm,
  },
  viewAllButton: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: 6,
    borderRadius: tokens.borderRadius.md,
    backgroundColor: 'transparent',
  },
  viewAllText: {
    ...tokens.typography.label.largeEmphasised,
    color: role.textPrimary,
  },
}));
